/* Copyright (c) 2023-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { FocusMonitor, InteractivityChecker, FocusOrigin, FocusOptions } from '@angular/cdk/a11y';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { getElementAt } from 'utils/lib/dom/hit-test';
import { RestoreFocusOptions } from '../models/hig-modal-shared.types';
import { dispatchNavEvent } from './spatial-navigation/spatial-navigation';
import { SpatialNavigationService } from './spatial-navigation/spatial-navigation.service';
import { SpatialNavigationDirection } from './spatial-navigation/spatial-navigation.types';

/**
 * The policy used in searching focusable element.
 *
 * `initial` - Finds the first element marked with `cdkFocusInitial`. If none exists, finds the first focusable element.
 *
 * `first` - Finds the first focusable element.
 */
export type FocusSearchPolicy = 'initial' | 'first';

interface CloseableDialogRef {
    afterClosed: Function;
    afterDismissed?: never;
}

interface DismissableDialogRef {
    afterClosed?: never;
    afterDismissed: Function;
}

export type RestoreFocusFunction = () => void;

function manhattanDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.abs(x1 - x2) + Math.abs(y1 - y2);
}

/**
 * A service that maintains the focus state.
 **/
@Injectable({
    providedIn: 'root'
})
export class FocusManager {
    private logger: Logger;

    public get currentFocusedElement(): HTMLElement {
        return document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    }

    constructor(
        loggingService: LoggingService,
        private spatialNavigationService: SpatialNavigationService,
        private inputModalityService: InputModalityService,
        private checker: InteractivityChecker,
        private focusMonitor: FocusMonitor
    ) {
        this.logger = loggingService.getLogger('FocusManager');
    }

    private canFocusElement(element: HTMLElement, origin: FocusOrigin): boolean {
        if (!element) {
            return false;
        }
        // because sometimes moving focus is triggered asynchronously, there are rare cases that the element is removed from DOM
        // when the function is called. check isConnected to ensure the element is still in current DOM.
        if (!element.isConnected) {
            this.logger.warn(`${element.nodeName} is not connected to DOM.`);
            return false;
        } else if (this.currentFocusedElement === element && element.classList.contains(`.cdk-${origin}-focused`)) {
            this.logger.warn(`cdk-${origin}-focused is existing in ${element.nodeName}.`);
            return false;
        }
        return true;
    }

    /**
     * Retrieve the first focusable element encountered in the given element's children.
     *
     * @param element
     * @param filterFunc provide a function to check whether an element is focusable. if
     * not provided, we use InterActivityChecker#isFocusable as default.
     * @returns the first focusable otherwise null
     *
     * Use SpatialNavigationService#isFocusable as filter function if calling this function is a result of some spatial navigation
     * operation
     */
    public getFirstFocusableChild(
        element: HTMLElement | Element,
        filterFunc?: (ele: HTMLElement) => boolean
    ): HTMLElement | null {
        const isFocusable = filterFunc ?? this.checker.isFocusable.bind(this.checker);
        const children = element.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            let focusable: HTMLElement = null;

            // angular InterActivityChecker considers elements with a valid tabindex focusable.
            // while spatial navigation polyfill only considers those elements with tabindex greater or equal than 0 focusable.
            // created a ticket AVENGERS-2433 for tracking this subtle difference as this results in moving focus
            // to an unexpected element
            if (child.nodeType === Node.ELEMENT_NODE && isFocusable(child as HTMLElement)) {
                focusable = child as HTMLElement;
            } else {
                focusable = this.getFirstFocusableChild(child, filterFunc);
            }

            if (focusable) {
                return focusable;
            }
        }

        return null;
    }

    /**
     * Find a focusable child element and focus on it.
     *
     * @param element the element to perform focusable search on
     * @param policy the desired search policy
     * @param options
     */
    public focusChild(
        element: HTMLElement,
        policy: FocusSearchPolicy,
        filterFunc?: (ele: HTMLElement) => boolean,
        options?: FocusOptions
    ) {
        if (element) {
            setTimeout(() => {
                const focusable =
                    policy === 'initial'
                        ? this.getInitialFocusable(element, filterFunc)
                        : this.getFirstFocusableChild(element, filterFunc);
                this.focusViaLastOrigin(focusable, options);
            }, 0);
        }
    }

    /**
     * Retrieve the initial focusable element in the given element's children.
     *
     * @param element
     * @param filterFunc provide a function to check whether an element is focusable. if
     * not provided, we use InterActivityChecker#isFocusable as default.
     * @returns the initial focusable if found, otherwise null.
     *
     * the initial focusable is retrieved in the following order
     * 1. an element with [cdkFocusInitial] attribute
     * 2. if none found, get the first focusable in its child tree
     */
    public getInitialFocusable(element: HTMLElement, filterFunc?: (ele: HTMLElement) => boolean): HTMLElement | null {
        const isFocusable = filterFunc ?? this.checker.isFocusable.bind(this.checker);
        let target: HTMLElement = element.querySelector('[cdkFocusInitial]');
        if (!target || !isFocusable(target)) {
            target = this.getFirstFocusableChild(element, isFocusable);
        }
        return target;
    }

    public focusViaLastOrigin(element: HTMLElement, options?: FocusOptions) {
        const origin = this.inputModalityService.lastFocusOrigin ?? 'keyboard';
        this.focusVia(element, origin, options);
    }

    public focusVia(element: HTMLElement, origin: FocusOrigin, options?: FocusOptions) {
        if (!this.canFocusElement(element, origin)) {
            return;
        }

        // Clears starting point
        if (this.spatialNavigationService.enabled) {
            this.spatialNavigationService.setStartingPoint(null);
        }

        this.focusMonitor.focusVia(element, origin, options);
    }

    /**
     * Navigate to the target element from a certain direction
     *
     * the function emulates spatial-navigation-polyfill's navigate behavior. it dispatches a "navbeforefocus"
     * event before moving the focus. If the event is default prevented, the function does nothing.
     * please check https://www.w3.org/TR/css-nav-1/#event-type-navbeforefocus for further details
     */
    public navigateTo(target: HTMLElement, dir: SpatialNavigationDirection, options?: FocusOptions) {
        if (!target || !dispatchNavEvent(target, 'navbeforefocus', dir)) {
            return;
        }
        this.focusViaLastOrigin(target, options);
    }

    /**
     * Gets the focusable element at (x, y).
     * If the element found at (x, y) is not focusable, gets the closest element within its spatial navigation container.
     *
     * @param x in viewport coordinate
     * @param y in viewport coordinate
     * @returns the focusable element. `undefined` if not found
     */
    private getFocusRestorationTargetAt(x: number, y: number): HTMLElement | undefined {
        const element = getElementAt(x, y);
        if (!element || this.checker.isFocusable(element)) {
            return element;
        }

        if (!Element.prototype.spatialNavigationSearch || !Element.prototype.focusableAreas) {
            return undefined;
        }

        const container = element.getSpatialNavigationContainer() as Element;
        const focusables = container.focusableAreas({ mode: 'visible' });

        let closestFocusable: HTMLElement;
        let minDistance = Number.POSITIVE_INFINITY;
        focusables.forEach(focusable => {
            if (!(focusable instanceof HTMLElement)) {
                return;
            }

            if (
                window.__spatialNavigation__.isContainer(focusable) &&
                focusables.some(node => focusable.contains(node))
            ) {
                return;
            }

            const rect = focusable.getBoundingClientRect();
            const centerX = (rect.left + rect.right) / 2;
            const centerY = (rect.top + rect.bottom) / 2;
            // todo: this distance function might not find the best target when focusables have varying sizes. revisit when it becomes a real problem
            const distance = manhattanDistance(x, y, centerX, centerY);
            if (distance < minDistance) {
                minDistance = distance;
                closestFocusable = focusable;
            }
        });
        return closestFocusable;
    }

    /**
     * Creates a function that attempts to restore focus onto `restoreTarget` when being called.
     * If `restoreTarget` is removed from DOM or becomes unfocusable,
     * the function will attempt to focus the nearest focusable from where `restoreTarget` was.
     *
     * @param restoreTarget the focus restoration target
     * @returns A function that attempts to restore focus when being called
     */
    public createRestoreFocusFunction(restoreTarget: HTMLElement): RestoreFocusFunction {
        if (restoreTarget === document.body) {
            return () => {
                this.logger.warn(
                    'restoreTarget cannot be BODY. The focus might have already been lost before focus restoration'
                );
            };
        }

        const targetRect = restoreTarget.getBoundingClientRect();
        return () => {
            if (restoreTarget.isConnected && this.checker.isFocusable(restoreTarget)) {
                this.focusViaLastOrigin(restoreTarget);
            } else {
                const x = (targetRect.left + targetRect.right) / 2;
                const y = (targetRect.top + targetRect.bottom) / 2;

                const element = this.getFocusRestorationTargetAt(x, y);
                if (element) {
                    this.focusViaLastOrigin(element);
                } else {
                    this.logger.warn(
                        'Potential focus loss (failed to restore focus) - target cannot be focused and no other focusable element is found'
                    );
                }
            }
        };
    }

    /**
     * Opens a modal (dialog or bottom sheet) with focus restoration.
     * The focus restoration algorithm will attempt to restore focus after the modal is closed.
     *
     * @param componentFactory the factory method which opens a modal
     * @return reference of the opened modal
     **/
    public restoreFocusAfterClosed<T extends CloseableDialogRef | DismissableDialogRef>(
        componentFactory: () => T,
        options?: RestoreFocusOptions
    ): T {
        // Get current focus prior to dialog/bottom-sheet being opened
        const restoreTarget = options?.restoreFocusTarget ?? this.currentFocusedElement;
        const componentRef = componentFactory();
        if (restoreTarget) {
            const restoreFocus = this.createRestoreFocusFunction(restoreTarget);
            let afterClosed$: Observable<any>;

            if (componentRef?.afterClosed) {
                afterClosed$ = componentRef.afterClosed();
            } else if (componentRef?.afterDismissed) {
                afterClosed$ = componentRef.afterDismissed();
            } else {
                throw new Error('not a supported type.');
            }
            if (!afterClosed$) {
                throw new Error('afterClosed$ is not assigned.');
            }

            afterClosed$.pipe(take(1)).subscribe(() => {
                restoreFocus();
            });
        }

        return componentRef;
    }
}
